import { Tag, Handshake, Coffee, Star, DollarSign, Send, Trophy } from "lucide-react";
import { computeStats, topOf, CATS, fmtVal } from "@/lib/chartUtils";

const ICONS = { tags: Tag, referrals: Handshake, oneOnOnes: Coffee, guests: Star, closedRx: DollarSign, closedTx: Send };

export default function StatsView({ events, members, title }) {
  const stats = computeStats(events);
  const allPeople = [...new Set([...members, ...Object.keys(stats)])];

  return (
    <div data-testid="stats-view">
      {/* Leader cards */}
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-orange-600" />
        <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase">Leaders — {title}</h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
        {CATS.map(c => {
          const l = topOf(stats, c.key);
          const Icon = ICONS[c.key] || Tag;
          return (
            <div key={c.key} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 text-center">
              <Icon className="w-5 h-5 mx-auto mb-1 text-zinc-400" />
              <p className="text-[9px] font-bold text-zinc-400 tracking-widest uppercase mb-1">{c.label}</p>
              <p className="text-sm font-extrabold text-zinc-950">{l ? l.name : "—"}</p>
              <p className="text-lg font-black text-orange-600">{l ? fmtVal(l.val, c.money) : "–"}</p>
            </div>
          );
        })}
      </div>

      {/* Breakdown table */}
      <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mb-3 pb-2 border-b border-zinc-100">
        Breakdown by Member
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="border-b-2 border-zinc-100">
              <th className="text-left px-3 py-2 text-[9px] font-bold text-zinc-400 tracking-widest uppercase">Member</th>
              {CATS.map(c => {
                const Icon = ICONS[c.key] || Tag;
                return (
                  <th key={c.key} className="text-left px-3 py-2 text-[9px] font-bold text-zinc-400 tracking-widest uppercase whitespace-nowrap">
                    <span className="inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {c.label}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {allPeople.map((name) => {
              const s = stats[name] || { tags: 0, referrals: 0, oneOnOnes: 0, guests: 0, closedRx: 0, closedTx: 0 };
              return (
                <tr key={name} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors">
                  <td className="px-3 py-2 font-bold text-zinc-950">{name}</td>
                  {CATS.map(c => {
                    const v = s[c.key] || 0;
                    const isTop = topOf(stats, c.key)?.name === name && v > 0;
                    return (
                      <td key={c.key} className={`px-3 py-2 ${isTop ? 'text-orange-600 font-extrabold' : v > 0 ? 'text-zinc-950' : 'text-zinc-300'}`}>
                        {fmtVal(v, c.money)}{isTop ? " ★" : ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-zinc-200 bg-zinc-50">
              <td className="px-3 py-2.5 font-extrabold text-zinc-950 text-xs uppercase tracking-wider">Total</td>
              {CATS.map(c => {
                const total = allPeople.reduce((sum, name) => {
                  const s = stats[name] || { tags: 0, referrals: 0, oneOnOnes: 0, guests: 0, closedRx: 0, closedTx: 0 };
                  return sum + (s[c.key] || 0);
                }, 0);
                return (
                  <td key={c.key} className="px-3 py-2.5 font-black text-orange-600">
                    {fmtVal(total, c.money)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Closed business detail */}
      {events.filter(e => e.type === "biz").length > 0 && (
        <>
          <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mt-6 mb-3 pb-2 border-b border-zinc-100">
            Closed Business Detail
          </h3>
          {events.filter(e => e.type === "biz").map((e, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-zinc-50 rounded-md mb-1.5">
              <span className="text-sm text-zinc-500 font-semibold">{e.sender}</span>
              <span className="text-zinc-300">→</span>
              <span className="text-sm font-bold text-zinc-950 flex-1">{e.receiver}</span>
              <span className="text-base font-black text-red-700">${Number(e.amt).toLocaleString()}</span>
            </div>
          ))}
        </>
      )}

      {events.length === 0 && (
        <p className="text-center text-zinc-400 py-8 text-sm">No events for this period.</p>
      )}
    </div>
  );
}
